import { Alert, FormHelperText } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControls, FormikArray, FormikNumericField } from 'components';
import { TranslationNamespace } from 'i18n';
import { PayoutDistributionRange } from 'types';

import { payoutDistributionRangesUtils } from './payout-distribution-ranges.utils';

const NAME = 'payoutDistributionRanges';

type Props = {
  fiatCurrencyCode?: string;
};

type Values = {
  payoutOrderMinAmount?: number;
  payoutOrderMaxAmount?: number;
  payoutDistributionRanges?: PayoutDistributionRange[];
};

export const PayoutDistributionRanges: React.FC<Props> = ({
  fiatCurrencyCode,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { values } = useFormikContext<Values>();

  const renderItemComponent = useCallback(
    ({ index }: { index: number }) => (
      <FormControls row sx={{ flex: 1 }}>
        <FormikNumericField
          label={t('sections.payout_distribution_ranges.fields.min_amount')}
          name={`${NAME}[${index}].minAmount`}
          required
          fullWidth
          allowNegative={false}
          suffix={fiatCurrencyCode}
        />
        <FormikNumericField
          label={t('sections.payout_distribution_ranges.fields.max_amount')}
          name={`${NAME}[${index}].maxAmount`}
          fullWidth
          allowNegative={false}
          suffix={fiatCurrencyCode}
        />
      </FormControls>
    ),
    [t, fiatCurrencyCode],
  );

  const hasOverlaps = useMemo(
    () =>
      payoutDistributionRangesUtils.hasOverlappingRanges(
        values.payoutDistributionRanges,
      ),
    [values.payoutDistributionRanges],
  );

  const noLimit = useMemo(
    () => t('sections.payout_distribution_ranges.no_limit'),
    [t],
  );
  const hasShopLimits = useMemo(
    () => !!values.payoutOrderMinAmount || !!values.payoutOrderMaxAmount,
    [values.payoutOrderMinAmount, values.payoutOrderMaxAmount],
  );

  return (
    <Fragment>
      <FormHelperText>
        {t('sections.payout_distribution_ranges.description')}{' '}
        <b>{t('sections.payout_distribution_ranges.no_ranges')}</b>
      </FormHelperText>
      {hasShopLimits && (
        <FormHelperText>
          {t('sections.payout_distribution_ranges.shop_limits', {
            min: values.payoutOrderMinAmount || noLimit,
            max: values.payoutOrderMaxAmount || noLimit,
          })}
        </FormHelperText>
      )}
      <FormikArray
        addItemLabel={t('buttons.add_range')}
        name={NAME}
        removable
        defaultValue={{ minAmount: '', maxAmount: '' }}
        ItemComponent={renderItemComponent}
      />
      {hasOverlaps && (
        <Alert severity="warning">
          {t('sections.payout_distribution_ranges.overlap_warning')}
        </Alert>
      )}
    </Fragment>
  );
};
